/**
 * Formatting helpers for rendering a gate's `detailsJson` into the expanded
 * gate row. Domain-neutral: each domain's `WorkspaceGateSpec.formatDetails`
 * composes these rather than the shell branching on gate keys.
 */

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const formatNumber = (value: number): string =>
  value.toLocaleString("en-US");

export const formatCount = (
  count: number,
  singular: string,
  plural: string
): string => `${formatNumber(count)} ${count === 1 ? singular : plural}`;

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

export const isStringRecord = (
  value: unknown
): value is Record<string, string> =>
  isRecord(value) &&
  Object.values(value).every((item) => typeof item === "string");

export const formatList = (items: string[]): string =>
  items.length > 0 ? items.join("\n") : "None";

const humanizeDetailKey = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase();
  return spaced.length > 0 ? spaced[0].toUpperCase() + spaced.slice(1) : key;
};

const formatReadableValue = (value: unknown): string | null => {
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return formatNumber(value);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (isStringArray(value)) {
    return formatList(value);
  }
  if (isStringRecord(value)) {
    return Object.entries(value)
      .map(([key, item]) => `${key} ${item}`)
      .join("\n");
  }
  return null;
};

/**
 * Keys the last-resort dump must never surface.
 *
 * `reason` is a closed discriminator the backend stamps for consumers to bind
 * to — deliberately not English. Printed raw it reads as the verdict and
 * contradicts the gate's own `result`: an `ai_tests_generated` row whose
 * `result` says "Generated a new rule test suite (no saved generated suite)"
 * showed "Reason: No saved generated suite", which reads as a failure on an
 * artifact that was in fact produced. The gate's `result` is the
 * human-readable line; a domain formatter is how a reason becomes prose.
 *
 * `testSuite` is a whole generated source file, which is not a row detail.
 */
const OPAQUE_DETAIL_KEYS = new Set(["reason", "testSuite"]);

/** Last-resort rendering: every readable key/value in the details blob. */
export const formatGenericDetails = (
  details: Record<string, unknown>
): string | null => {
  const lines = Object.entries(details).flatMap(([key, value]) => {
    if (OPAQUE_DETAIL_KEYS.has(key)) {
      return [];
    }
    const formatted = formatReadableValue(value);
    return formatted ? [`${humanizeDetailKey(key)}: ${formatted}`] : [];
  });
  return lines.length > 0 ? lines.join("\n") : null;
};

/**
 * Renders `detailsJson.failures` (a suite runner's failed-test list) as
 * `name\nmessage` blocks. `stripTitlePrefix` removes the runner's own describe
 * prefix so the row shows the test name the user actually wrote.
 */
export const formatTestSuiteFailures = (
  failures: unknown,
  stripTitlePrefix?: RegExp
): string | null => {
  if (!Array.isArray(failures)) {
    return null;
  }

  const failureLines = failures.flatMap((failure, index) => {
    if (!isRecord(failure)) {
      return [];
    }

    const rawName =
      typeof failure.testName === "string" && failure.testName.length > 0
        ? failure.testName
        : null;
    const testName =
      rawName === null
        ? `Failure ${index + 1}`
        : (stripTitlePrefix?.exec(rawName)?.[1] ?? rawName);
    const message =
      typeof failure.message === "string" && failure.message.length > 0
        ? failure.message
        : null;

    return [message ? `${testName}\n${message}` : testName];
  });

  return failureLines.length > 0 ? failureLines.join("\n\n") : null;
};

/** "12 ai-generated tests passed" — `source` names who authored the suite. */
export const formatSuiteCountMessage = (
  details: Record<string, unknown>,
  source: string,
  passed: boolean
): string | null => {
  const testCount = details.testCount;
  if (typeof testCount !== "number" || !Number.isFinite(testCount)) {
    return null;
  }

  return `${formatNumber(testCount)} ${source.toLowerCase()} ${
    testCount === 1 ? "test" : "tests"
  } ${passed ? "passed" : "ran"}`;
};

/**
 * Renders a required-checks gate's `detailsJson.failing` — entries of
 * `{ key, detail? }` — as `check name\nwhat went wrong` blocks.
 *
 * The gate's own `result` only counts the failures ("1 required check needs
 * attention"); the entries are the sole carrier of which check failed and why,
 * so an expandable row has to read them directly.
 */
export const formatFailedChecks = (failing: unknown): string | null => {
  if (!Array.isArray(failing)) {
    return null;
  }

  const blocks = failing.flatMap((entry) => {
    if (!isRecord(entry) || typeof entry.key !== "string") {
      return [];
    }

    const name = humanizeDetailKey(entry.key);
    const detail = [entry.detail, entry.message].find(
      (candidate): candidate is string =>
        typeof candidate === "string" && candidate.length > 0
    );
    return [detail ? `${name}\n${detail}` : name];
  });

  return blocks.length > 0 ? blocks.join("\n\n") : null;
};
